import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Minus, Waves, Activity, Zap, Droplet, Thermometer, Wrench, MonitorSmartphone, Layers } from "lucide-react";
import TopBar from "../components/shop/TopBar";
import Reveal from "../components/Reveal";
import { SHOP_SPECS, SHOP_STEPS, SHOP_COMPARE } from "../data/shop";
import { PROBES, STRIP_A, STRIP_B, STRIP_C, VARIANTS, SERVICE, TESTER_IMG } from "../data/build";

const LINKS = [
  { to: "/shop", label: "Pool Shop" },
  { to: "/shop/build", label: "Build Spec" },
  { to: "/shop/interior", label: "Interior" },
  { to: "/shop/brief", label: "Brief / RFQ" },
  { to: "/software", label: "Software" },
  { to: "/home", label: "Home System" },
];

const PROBE_ICON = { P1: Zap, P2: Droplet, P3: Waves, P4: Thermometer };
const STRIPS = [
  { s: STRIP_A, tag: "Always", accent: "#0891B2" },
  { s: STRIP_B, tag: "Leafy / phosphate-prone", accent: "#0EA5E9" },
  { s: STRIP_C, tag: "Mineral / magnesium pool", accent: "#7C3AED" },
];

export default function ShopConcept() {
  return (
    <div className="min-h-screen bg-[#FAFCFD]" data-testid="shop-concept-page">
      <TopBar links={LINKS} />

      {/* Hero */}
      <section className="relative pt-14 pb-16 md:pt-20 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50/70 to-white" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-mono2 text-sky-700 mb-5">
              <Activity className="h-3.5 w-3.5" /> Project 1 · In-store &amp; portable water tester
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              The all-in-1 <span className="aqua-text">water testing</span> unit
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-xl">
              A benchtop tester built on <strong>four continuous probes</strong> and <strong>three top-loaded strip slots</strong> —
              no liquid reagents, no disks. It lights the strips each pool needs, reads the full panel in about
              <strong> 60–90 seconds</strong>, flood-cleans itself between customers, and syncs straight to the Pool Software.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/shop/build" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="shop-build-link">
                Build spec &amp; internals <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/software" className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-sky-300 text-slate-800 px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="shop-open-software-btn">
                <MonitorSmartphone className="h-4 w-4" /> See the Software
              </Link>
              <a href="#compare" className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-sky-300 text-slate-800 px-6 py-3.5 rounded-full font-semibold transition-colors">
                vs the Spin Disk
              </a>
              <Link to="/shop/brief" className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="shop-to-brief-btn">
                Manufacturing brief (RFQ)
              </Link>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="rounded-3xl overflow-hidden border border-slate-200 shadow-[0_40px_80px_-40px_rgba(3,105,161,0.4)] bg-white">
            <img src={TESTER_IMG.front} alt="Aqualux Nexus Pool Shop tester — front" className="w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Specs */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3">At a glance</p><h2 className="font-display text-3xl font-bold text-slate-900">Specifications</h2></Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SHOP_SPECS.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.04}>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`spec-${i}`}>
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mb-1">{s.k}</p>
                  <p className="font-semibold text-slate-800">{s.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What it measures — probes + strips */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="measures-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-3">What it measures</p>
            <h2 className="font-display text-3xl font-bold text-slate-900">Four live probes, three smart strips</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">The probes read continuously with no consumables. The three 6 mm strip slots add everything a probe can't — and the unit lights only the strips a given pool actually needs.</p>
          </Reveal>

          {/* Probes */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PROBES.map((p, i) => {
              const Icon = PROBE_ICON[p.p] || Activity;
              return (
                <Reveal key={p.p} delay={i * 0.04}>
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`probe-${p.p}`}>
                    <span className="h-10 w-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${p.color}1A`, border: `1px solid ${p.color}55` }}>
                      <Icon className="h-5 w-5" style={{ color: p.color }} />
                    </span>
                    <p className="font-display font-bold text-slate-900">{p.name}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{p.method}</p>
                    <span className="mt-3 inline-block text-[10px] font-mono2 uppercase tracking-widest text-emerald-600">Continuous · no reagent</span>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Strips */}
          <div className="mt-4 grid md:grid-cols-3 gap-3">
            {STRIPS.map(({ s, tag, accent }, i) => (
              <Reveal key={s.port} delay={i * 0.05}>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`strip-${s.port}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center gap-2 font-display font-bold text-slate-900"><Layers className="h-4 w-4" style={{ color: accent }} /> Slot {s.port}</span>
                    <span className="h-3 w-3 rounded-full" style={{ background: accent, boxShadow: `0 0 0 3px ${accent}22` }} title="prompt LED" />
                  </div>
                  <p className="font-semibold text-slate-800">{s.label}</p>
                  <ul className="mt-2 space-y-1">
                    {s.reads.map((r) => <li key={r.t} className="text-sm text-slate-500 flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-sky-500 mt-0.5 shrink-0" />{r.t}</li>)}
                  </ul>
                  <span className="mt-3 inline-block text-[10px] font-mono2 uppercase tracking-widest text-slate-400">Prompt: {tag}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Two variants */}
      <section className="py-16 md:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-white">
              <img src={TESTER_IMG.rear} alt="Aqualux Nexus Pool Shop tester — rear I/O" className="w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <p className="eyebrow mb-3">One core, two builds</p>
              <h2 className="font-display text-3xl font-bold text-slate-900">Countertop or portable</h2>
              <p className="mt-3 text-slate-600">Identical measurement core — they differ only in how they handle rinse water and power.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {VARIANTS.map((v) => (
                  <div key={v.key} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`variant-${v.key}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />
                      <p className="font-display font-bold text-slate-900">{v.name}</p>
                    </div>
                    <p className="text-xs font-mono2 uppercase tracking-widest text-slate-400 mt-0.5">{v.tag}</p>
                    <dl className="mt-3 space-y-1.5 text-sm">
                      <div><dt className="text-slate-400 inline">Rinse: </dt><dd className="text-slate-700 inline">{v.rinse}</dd></div>
                      <div><dt className="text-slate-400 inline">Waste: </dt><dd className="text-slate-700 inline">{v.waste}</dd></div>
                      <div><dt className="text-slate-400 inline">Power: </dt><dd className="text-slate-700 inline">{v.power}</dd></div>
                    </dl>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3">How it works</p><h2 className="font-display text-3xl font-bold text-slate-900">From beaker to branded report</h2></Reveal>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {SHOP_STEPS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6" data-testid={`step-${i}`}>
                  <span className="font-mono2 text-sm text-sky-600 font-bold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-display font-bold text-slate-900 mt-1">{s.t}</p>
                  <p className="text-sm text-slate-500 mt-1.5">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-6 rounded-2xl border border-sky-100 bg-sky-50/60 p-5 flex items-start gap-3" data-testid="service-note">
              <span className="h-9 w-9 rounded-lg bg-white border border-sky-100 flex items-center justify-center shrink-0"><Wrench className="h-4 w-4 text-sky-600" /></span>
              <p className="text-sm text-slate-600"><span className="font-semibold text-slate-800">Maintenance &amp; calibration — </span>{SERVICE.replace("Maintenance & calibration: ", "")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" className="py-16 md:py-24 bg-[#0A0F1D] text-white">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3 text-sky-400">Honest comparison</p><h2 className="font-display text-3xl font-bold">Aqualux Nexus Shop vs the Spin Disk</h2>
            <p className="mt-3 text-slate-400 max-w-2xl">The Spin Disk is fast — but every test burns a proprietary disk. We match it on speed with four continuous probes plus a single low-cost universal strip, and win decisively on cost-per-test, consumables and openness.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10 font-mono2 text-[11px] uppercase tracking-widest text-slate-400">
                    <th className="p-4"> </th><th className="p-4 text-sky-300">Aqualux Nexus Shop</th><th className="p-4">Spin Disk (WaterLink)</th>
                  </tr>
                </thead>
                <tbody>
                  {SHOP_COMPARE.map((r) => (
                    <tr key={r.row} className="border-b border-white/5">
                      <td className="p-4 text-slate-300 font-medium">{r.row}</td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-2 text-white">
                          {r.win === true ? <Check className="h-4 w-4 text-emerald-400" /> : r.win === false ? <Minus className="h-4 w-4 text-slate-500" /> : null}
                          {r.ours}
                        </span>
                      </td>
                      <td className="p-4 text-slate-400">{r.disk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/software" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white px-7 py-3.5 rounded-full font-semibold transition-colors" data-testid="shop-compare-software-btn">
                <MonitorSmartphone className="h-4 w-4" /> See it in the Software
              </Link>
              <Link to="/shop/build" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/15 text-white px-7 py-3.5 rounded-full font-semibold transition-colors" data-testid="shop-compare-build-btn">
                Full build spec <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
